// Restore only attendee progress. Registration, credentials, system controls,
// executable apps and wallet keys must come from the destination badge.
const GAME_APPS = new Set(
  "67 snake 2048 blackjack breakout tetris flappy dance hopper invaders jetpack outrun whack reaction mines hangman pong slots maze bubblewrap chess tictactoe simon sokoban".split(
    " "
  )
);
const PUBLIC_APPS = new Set([
  ...Array.from(GAME_APPS),
  ..."blocks conversation etch eightball flashlight fortune hardwarephysics kaleido ktnh lava level lightshow teardown".split(
    " "
  ),
]);
const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export function badgeRestorePlan(raw: string) {
  if (new TextEncoder().encode(raw).length > 65536)
    throw new Error("Backup is too large to restore.");
  const snapshot: unknown = JSON.parse(raw);
  if (
    !isObject(snapshot) ||
    snapshot.v !== 1 ||
    snapshot.kind !== "fs_mirror" ||
    !isObject(snapshot.files)
  )
    throw new Error("This backup format cannot be restored.");
  const files: { path: string; text: string }[] = [];
  let launcherOrder: string[] = [];
  let pals: string[] | undefined;
  let skipped = 0;
  const seen = new Set<string>();
  for (const [rawPath, entry] of Object.entries(snapshot.files)) {
    const path = rawPath.replace(/^\/littlefs\//, "");
    if (seen.has(path))
      throw new Error("Backup contains ambiguous file paths.");
    seen.add(path);
    if (
      !isObject(entry) ||
      entry.trunc ||
      entry.skip ||
      typeof entry.text !== "string" ||
      new TextEncoder().encode(entry.text).length > 4096
    ) {
      ++skipped;
      continue;
    }
    if (path === "solana.json") {
      const wallet: unknown = JSON.parse(entry.text);
      if (isObject(wallet) && Array.isArray(wallet.all_items))
        pals = Array.from(
          new Set(
            wallet.all_items.filter(
              (id): id is string => typeof id === "string" && /^[1-9]$/.test(id)
            )
          )
        );
      continue;
    }
    if (path === "config/system.cfg") {
      const matches = entry.text
        .split(/\r?\n/)
        .filter((line) => line.startsWith("launcher_order="));
      if (matches.length === 1)
        launcherOrder = Array.from(
          new Set(
            matches[0]
              .slice(15)
              .split(",")
              .filter((slug) => PUBLIC_APPS.has(slug))
          )
        );
      continue;
    }
    const app = /^config\/([a-z0-9_-]+)\.cfg$/.exec(path)?.[1];
    if (
      (app && GAME_APPS.has(app)) ||
      /^config\/(?:contacts\/[a-zA-Z0-9_-]{1,63}|sponsors\/\d{2,5}|workshops\/[a-z0-9_-]{1,63})\.cfg$/.test(
        path
      )
    ) {
      files.push({ path, text: entry.text });
    }
  }
  return {
    files,
    launcherOrder,
    pals,
    partial: snapshot.complete === false || skipped > 0,
  };
}
